import axios from 'axios';

const baseURL = process.env.REACT_APP_API_URL || 'https://ngozi-university-backend.vercel.app/api';

/** Store reference set by App to avoid circular dependency (store -> slices -> publicApi -> api -> store). */
let storeRef = null;
export function setApiStore(store) {
  storeRef = store;
}

const api = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const store = storeRef;
  if (store) {
    const state = store.getState();
    const token = state.auth?.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

let refreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => (token ? prom.resolve(token) : prom.reject(error)));
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const store = storeRef;
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && store) {
      if (refreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }
      originalRequest._retry = true;
      refreshing = true;
      const refreshToken = store.getState().auth?.refreshToken;
      if (!refreshToken) {
        store.dispatch({ type: 'auth/logout' });
        return Promise.reject(error);
      }
      try {
        const { data } = await axios.post(`${baseURL}/auth/refresh`, { refreshToken });
        store.dispatch({
          type: 'auth/setCredentials',
          payload: { accessToken: data.accessToken, refreshToken: data.refreshToken || refreshToken },
        });
        processQueue(null, data.accessToken);
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        store.dispatch({ type: 'auth/logout' });
        return Promise.reject(refreshError);
      } finally {
        refreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default api;
